import { useContext, useMemo, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { InputText } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { isEmpty as _isEmpty } from "lodash"

import { Legends } from "../../../components/graphs/Legends"
import { LineGraph } from "../../../components/graphs/Line/LineGraph"
import { KonanAPI } from "../../../services/KonanAPI"
import type { FeaturesCountsInsight } from "../../../types/generated/api/FeaturesCountsInsight"
import { InsightList } from "../../../types/generated/api/InsightList"
import { FEATURE_NESTING_SEPARATOR } from "../../../utils/constants"
import { ReportContext } from "../Context"
import type { InsightProps } from "../interfaces"

/**
 * DistributionCurveContainer Component
 *
 * Visualizes the distribution of numerical features across different labels,
 * enabling comparative analysis of feature patterns.
 *
 * Features:
 * - Multi-select functionality for both labels and features
 * - Dynamic curve generation for selected combinations
 * - Intelligent handling of selection states
 * - Responsive legend display
 *
 * Technical details:
 * - Processes raw data into plottable curve points
 * - Handles edge cases in data transformation
 * - Maintains selection state consistency
 *
 * @param {InsightProps} props - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {React.ReactElement} A Grid item containing the distribution curve visualization
 */
export const DistributionCurveContainer = (props: InsightProps): React.ReactElement => {
  const { insight } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { report } = useContext(ReportContext)

  const { t } = useTranslation("insights")

  const [selectedLabels, setSelectedLabels] = useState<{
    labels: string[] | undefined
    areAllValuesSelected: boolean
  }>({
    labels: report?.labels?.map((label) => label?.label)?.filter((label) => label !== "NA") ?? undefined,
    areAllValuesSelected: true,
  })

  const [selectedFeatures, setSelectedFeatures] = useState<{
    features: string[] | undefined
    areAllValuesSelected: boolean
  }>({ features: report?.features?.numerical ?? undefined, areAllValuesSelected: true })

  // Fetch distribution curve insight data from the API
  const { data: distributionCurveInsight, isLoading: isLoadingFeaturesCountsInsight } = useQuery<
    AxiosResponse<FeaturesCountsInsight>,
    AxiosError
  >(
    [
      "report-distribution-curve",
      projectId,
      report?.uuid,
      insight.uuid,
      selectedLabels.labels,
      selectedFeatures.features,
    ],
    () =>
      KonanAPI.fetchFeaturesCountInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
        labels: selectedLabels.labels ?? undefined,
        features: selectedFeatures.features,
        features_category: "numerical",
      }),
    {
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  const isEmpty = _isEmpty(distributionCurveInsight?.data.features_counts_per_label)

  // Process the fetched data to create distribution curve data
  const distributionCurveData = useMemo(() => {
    // Early return for loading state
    if (!distributionCurveInsight?.data?.features_counts_per_label) {
      return []
    }

    const data: Array<{ id: string; data: Array<{ x: number; y: number }> }> = []

    // Transform nested data structure into plottable format
    Object.entries(distributionCurveInsight.data.features_counts_per_label).forEach(([labelName, labelValue]) => {
      Object.entries(labelValue).forEach(([featureKey, featureValue]) => {
        // Skip empty feature values
        if (Object.keys(featureValue).length !== 0) {
          // Create new data series for this feature-label combination
          data.push({ id: `${featureKey.replaceAll(FEATURE_NESTING_SEPARATOR, ".")} (${labelName})`, data: [] })

          Object.entries(featureValue).forEach(([key, value]) => {
            // Handle binned data by averaging bin boundaries
            const xValue = key.includes("__")
              ? Number((key.split("__").reduce((acc, num) => acc + Number(num), 0) / 2).toFixed(2))
              : Number(Number(key).toFixed(2))

            // Add point to current series
            data[data.length - 1]?.data.push({
              x: xValue,
              y: value as number,
            })
          })

          // Sort points for proper line rendering
          data[data.length - 1]?.data.sort((a, b) => a.x - b.x)
        }
      })
    })

    return data
  }, [distributionCurveInsight?.data?.features_counts_per_label])

  const distributionCurveLines = useMemo(() => {
    return new Set(distributionCurveData.map((item) => item.id.replaceAll(FEATURE_NESTING_SEPARATOR, "."))) // Flattens the array to a 1D array
  }, [distributionCurveData])

  /**
   * Handles the selection and deselection of labels in the distribution curve filter.
   *
   * This function manages three main scenarios:
   * 1. When all options are deselected: Resets to all labels being selected
   * 2. When "All Decisions" was previously selected: Switches to only the specifically selected labels
   * 3. When "All Decisions" is newly selected: Switches back to all labels being selected
   *
   * @param {string[]} selectedOptions - Array of currently selected label options from the dropdown
   *
   * @example
   * // When deselecting all options
   * handleLabelSelect([]) // Resets to all labels
   *
   * // When selecting specific labels after "All Decisions"
   * handleLabelSelect(['Label1', 'Label2']) // Sets only these specific labels
   *
   * // When selecting "All Decisions"
   * handleLabelSelect(['All Decisions']) // Resets to all available labels
   */
  const handleLabelSelect = (selectedOptions: string[]): void => {
    // If no options are selected, reset to all labels
    if (selectedOptions.length === 0) {
      setSelectedLabels({
        labels: report?.labels?.map((label) => label?.label)?.filter((label) => label !== "NA"),
        areAllValuesSelected: true,
      })
      return
    }

    // If previously all values were selected, filter to only specific selections
    if (selectedLabels.areAllValuesSelected) {
      setSelectedLabels({
        labels: selectedOptions.filter((option) => option !== t("DistributionCurve.filters.decisions.all")),
        areAllValuesSelected: false,
      })
      return
    }

    // Handle toggling between all labels and specific selections
    setSelectedLabels({
      labels: selectedOptions.includes(t("DistributionCurve.filters.decisions.all"))
        ? report?.labels?.map((label) => label?.label)?.filter((label) => label !== "NA")
        : selectedOptions,
      areAllValuesSelected: selectedOptions.includes(t("DistributionCurve.filters.decisions.all")),
    })
  }

  /**
   * Handles the selection and deselection of features in the distribution curve filter.
   *
   * This function manages three main scenarios:
   * 1. When all options are deselected: Resets to all numerical features being selected
   * 2. When "All Features" was previously selected: Switches to only the specifically selected features
   * 3. When "All Features" is newly selected: Switches back to all features being selected
   *
   * @param {string[]} selectedOptions - Array of currently selected feature options from the dropdown
   * @returns {void}
   *
   * @example
   * // When deselecting all options
   * handleFeatureSelect([]) // Resets to all features
   *
   * // When selecting specific features
   * handleFeatureSelect(['Feature1', 'Feature2']) // Sets only these features
   *
   * // When selecting "All Features"
   * handleFeatureSelect(['All Features']) // Resets to all available features
   */
  const handleFeatureSelect = (selectedOptions: string[]): void => {
    // If no options are selected, reset to all features
    if (selectedOptions.length === 0) {
      setSelectedFeatures({
        features: report?.features?.numerical ?? undefined,
        areAllValuesSelected: true,
      })
      return
    }

    // If previously all values were selected, filter to only specific selections
    if (selectedLabels.areAllValuesSelected) {
      setSelectedFeatures({
        features: selectedOptions.filter((option) => option !== t("DistributionCurve.filters.features.all")),
        areAllValuesSelected: false,
      })
      return
    }

    // Handle toggling between all features and specific selections
    setSelectedFeatures({
      features: selectedOptions.includes(t("DistributionCurve.filters.features.all"))
        ? report?.labels?.map((label) => label?.label)?.filter((label) => label !== "NA")
        : selectedOptions,
      areAllValuesSelected: selectedOptions.includes(t("DistributionCurve.filters.features.all")),
    })
  }

  return (
    <Grid item xs={12}>
      <LineGraph
        graphHeight={340}
        title={t("DistributionCurve.title")}
        tooltipText={<Trans>{t("DistributionCurve.tooltip")}</Trans>}
        data={distributionCurveData}
        isLoading={isLoadingFeaturesCountsInsight}
        customHeaderComponent={
          <InputText
            value={
              selectedLabels.areAllValuesSelected
                ? t("DistributionCurve.filters.decisions.all")
                : selectedLabels.labels?.length && selectedLabels.labels?.length > 0
                  ? t("DistributionCurve.filters.decisions.selected", { count: selectedLabels.labels?.length })
                  : t("DistributionCurve.filters.decisions.select")
            }
            multipleModeProps={{
              isActive: true,
              selectedValues: selectedLabels.areAllValuesSelected
                ? [t("DistributionCurve.filters.decisions.all")]
                : (selectedLabels.labels ?? [t("DistributionCurve.filters.decisions.all")]),
              setSelectedValues: (selectedOptions) => handleLabelSelect(selectedOptions as string[]),
              allowDeselect: !selectedLabels.areAllValuesSelected,
              hideChips: true,
            }}
            options={report?.labels?.map((label) => label?.label)?.filter((label) => label !== "NA") ?? []}
            isSelect
            hideDescription
            fullWidth
            menuProps={{ menuMaxContent: true }}
          />
        }
        subHeader={
          <Grid container item xs justifyContent={"space-between"}>
            <InputText
              optionsWithValues={(report?.features?.numerical ?? []).map((feature) => ({
                label: feature.replaceAll(FEATURE_NESTING_SEPARATOR, "."),
                value: feature,
              }))}
              value={
                selectedFeatures.areAllValuesSelected
                  ? t("DistributionCurve.filters.features.all")
                  : selectedFeatures.features?.length && selectedFeatures.features?.length > 0
                    ? t("DistributionCurve.filters.features.selected", { count: selectedFeatures.features?.length })
                    : t("DistributionCurve.filters.features.select")
              }
              multipleModeProps={{
                isActive: true,
                selectedValues: selectedFeatures.areAllValuesSelected
                  ? [t("DistributionCurve.filters.features.all")]
                  : (selectedFeatures.features ?? [t("DistributionCurve.filters.features.all")]),

                setSelectedValues: (selectedOptions) => handleFeatureSelect(selectedOptions as string[]),
                allowDeselect: !selectedFeatures.areAllValuesSelected,
                hideChips: true,
              }}
              width={300}
              isSelect
              hideDescription
              menuProps={{ menuMaxContent: true }}
            />

            {/* Custom labels */}
            <Grid item xs display={"flex"} gap={1} justifyContent={"end"} alignItems={"center"}>
              {/* TODO:: figure out a way to dynamically display the legends */}
              <Legends legends={[...(distributionCurveLines ?? [])]} slice={3} />
            </Grid>
          </Grid>
        }
        emptyState={
          insight.status !== InsightList.status.SUCCEEDED
            ? {
                title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                  defaultValue: insight.status,
                })?.toLocaleLowerCase()}`,
              }
            : isEmpty
              ? { title: t("DistributionCurve.emptyState.unavailable") }
              : report?.features?.numerical?.length === 0
                ? { title: t("DistributionCurve.emptyState.noNumericalData") }
                : undefined
        }
      />
    </Grid>
  )
}
